import * as Dialog from '@radix-ui/react-dialog';
import {
  flip,
  offset,
  shift,
  useFloating,
  type VirtualElement,
} from '@floating-ui/react';
import { X } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';

import { Button } from '@/components/Button/Button';

import { createRectLike } from './tourGeometry';
import type { TourStep, TourTargetRect } from './tourTypes';

import styles from './TourCallout.module.css';

interface TourCalloutProps {
  currentStep: number;
  isMobile: boolean;
  onBack: () => void;
  onDismiss: () => void;
  onPrimaryAction: () => void;
  rect: TourTargetRect | null;
  step: TourStep;
  // The control this step points at, where focus goes when the tour closes.
  target: HTMLElement | null;
  totalSteps: number;
}

export function TourCallout({
  currentStep,
  isMobile,
  onBack,
  onDismiss,
  onPrimaryAction,
  rect,
  step,
  target,
  totalSteps,
}: TourCalloutProps) {
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const isFirstStep = currentStep === 0;
  const isFinalStep = currentStep === totalSteps - 1;
  const virtualReference = useMemo<VirtualElement | null>(() => {
    if (!rect) {
      return null;
    }

    return {
      getBoundingClientRect: () => createRectLike(rect),
    };
  }, [rect]);
  const { floatingStyles, refs, update } = useFloating({
    middleware: [offset(14), flip({ padding: 16 }), shift({ padding: 16 })],
    placement: step.placement ?? 'bottom',
  });

  useEffect(() => {
    refs.setReference(isMobile ? null : virtualReference);

    if (!isMobile && virtualReference) {
      void update();
    }
  }, [isMobile, refs, update, virtualReference]);

  return (
    <Dialog.Root
      modal
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onDismiss();
        }
      }}
      open
    >
      <Dialog.Portal>
        <Dialog.Content
          className={styles.content}
          data-mobile={isMobile ? 'true' : 'false'}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            (target ?? returnFocusRef.current)?.focus();
          }}
          onOpenAutoFocus={() => {
            // Focus has not moved yet: this is what had it.
            returnFocusRef.current =
              document.activeElement instanceof HTMLElement ? document.activeElement : null;
          }}
          ref={refs.setFloating}
          style={isMobile ? undefined : floatingStyles}
        >
          <div className={styles.header}>
            <p className={styles.stepIndicator}>
              Step {currentStep + 1} of {totalSteps}
            </p>
            <Dialog.Close asChild>
              <button
                aria-label="Dismiss product tour"
                className={styles.closeButton}
                type="button"
              >
                <X aria-hidden="true" size={18} />
              </button>
            </Dialog.Close>
          </div>
          <Dialog.Title className={styles.title}>
            {step.title}
          </Dialog.Title>
          <Dialog.Description className={styles.body}>
            {step.body}
          </Dialog.Description>
          <div className={styles.actions}>
            {!isFirstStep ? (
              <Button onClick={onBack} variant="secondary">
                Back
              </Button>
            ) : null}
            <Button onClick={onPrimaryAction}>
              {isFinalStep ? 'Got it' : 'Next'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
